import React, { useState, useEffect, useRef } from 'react';
import { FaInfoCircle } from 'react-icons/fa';
import { isAdmin } from '../utils/auth';
import { isAcademyU13OrYounger } from '../utils/playerUtils';
import api from '../services/apiService';

function EditPlayerModal({ player, onClose, onSubmit }) {
    const [activeTab, setActiveTab] = useState('details');
    
    // Check if this is a friendly team - hide Parents tab for friendly teams
    const teamType = sessionStorage.getItem('selected_team_type');
    const isFriendlyTeam = teamType === 'friendly';
    const hideContactFields = isAcademyU13OrYounger();
    const [editForm, setEditForm] = useState({
        player_id: player.player_id,
        first_name: (player.first_name || '').trim(),
        last_name: (player.last_name || '').trim(),
        phone: (player.phone || '').trim(),
        email: (player.email || '').trim(),
        position: player.position || '',
        preferred_foot: player.preferred_foot || 'Right',
        shirt_number: player.shirt_number || '',
        pace: player.pace || 50,
        shooting: player.shooting || 50,
        passing: player.passing || 50,
        dribbling: player.dribbling || 50,
        defending: player.defending || 50,
        physical: player.physical || 50
    });
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [tabsWithErrors, setTabsWithErrors] = useState([]);
    const [adminStatus, setAdminStatus] = useState(false);
    const [shirtNumberError, setShirtNumberError] = useState('');
    const [isCheckingShirtNumber, setIsCheckingShirtNumber] = useState(false);
    const [linkedParents, setLinkedParents] = useState([]);
    const [isUnlinking, setIsUnlinking] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [parentToUnlink, setParentToUnlink] = useState(null);
    const [updatingPermission, setUpdatingPermission] = useState(false);
    const shirtNumberCheckTimer = useRef(null);

    useEffect(() => {
        return () => {
            if (shirtNumberCheckTimer.current) {
                clearTimeout(shirtNumberCheckTimer.current);
            }
        };
    }, []);

    useEffect(() => {
        // Check and log admin status for debugging
        const isAdminValue = isAdmin();
        const isAdminStorage = sessionStorage.getItem('is_admin');
        console.log('Is Admin Function Result:', isAdminValue);
        console.log('Is Admin Storage Value:', isAdminStorage);
        setAdminStatus(isAdminValue);
        
        // Parse parent data if available
        if (player.parent_data) {
            try {
                const parentData = typeof player.parent_data === 'string'
                    ? JSON.parse(player.parent_data)
                    : player.parent_data;
                    
                // Convert to array if it's a single object
                const parentsArray = Array.isArray(parentData) ? parentData : [parentData];
                setLinkedParents(parentsArray);
            } catch (e) {
                console.error('Error parsing parent data:', e);
                setLinkedParents([]);
            }
        }
    }, [player]);

    const validateForm = () => {
        const errors = {};
        const missingByTab = { details: [], stats: [] };

        if (!editForm.first_name.trim()) {
            errors.first_name = true;
            missingByTab.details.push('first name');
        }
        if (!editForm.last_name.trim()) {
            errors.last_name = true;
            missingByTab.details.push('last name');
        }
        if (!hideContactFields && !editForm.phone.trim()) {
            errors.phone = true;
            missingByTab.details.push('phone');
        }
        if (!editForm.position) {
            errors.position = true;
            missingByTab.stats.push('position');
        }

        return { errors, missingByTab, isValid: Object.keys(errors).length === 0 };
    };

    const buildValidationMessage = (missingByTab) => {
        const parts = [];
        if (missingByTab.details.length > 0) {
            parts.push(`Details tab (${missingByTab.details.join(', ')})`);
        }
        if (missingByTab.stats.length > 0) {
            parts.push(`Stats tab (${missingByTab.stats.join(', ')})`);
        }
        return `Please complete the required fields on the ${parts.join(' and ')}.`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setFieldErrors({});
        setTabsWithErrors([]);

        const { errors, missingByTab, isValid } = validateForm();
        if (!isValid) {
            setFieldErrors(errors);
            const errorTabs = [];
            if (missingByTab.details.length > 0) errorTabs.push('details');
            if (missingByTab.stats.length > 0) errorTabs.push('stats');
            setTabsWithErrors(errorTabs);
            setError(buildValidationMessage(missingByTab));
            setActiveTab(errorTabs[0]);
            return;
        }

        // Check shirt number availability before submitting
        if (editForm.shirt_number && shirtNumberError) {
            setError('Please fix the shirt number on the Details tab before saving');
            setActiveTab('details');
            return;
        }

        const existingHeight = player.height != null && Number(player.height) > 0
            ? Number(player.height)
            : null;

        const formToSubmit = {
            ...(hideContactFields
                ? {
                    ...editForm,
                    phone: (player.phone || '').trim(),
                    email: (player.email || '').trim(),
                }
                : editForm),
            first_name: editForm.first_name.trim(),
            last_name: editForm.last_name.trim(),
            phone: hideContactFields ? (player.phone || '').trim() : editForm.phone.trim(),
            email: hideContactFields ? (player.email || '').trim() : (editForm.email || '').trim(),
            height: existingHeight,
        };

        onSubmit(formToSubmit);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditForm(prev => ({
            ...prev,
            [name]: value
        }));
        if (fieldErrors[name]) {
            setFieldErrors(prev => {
                const next = { ...prev };
                delete next[name];
                return next;
            });
        }
        if (error) {
            setError('');
            setTabsWithErrors([]);
        }
    };

    const checkShirtNumberAvailability = async (value) => {
        if (!value) {
            setShirtNumberError('');
            return;
        }

        setIsCheckingShirtNumber(true);
        try {
            const teamId = sessionStorage.getItem('selected_team_id');
            const response = await api.get(`${process.env.REACT_APP_API_URL}/players/check_shirt_number.php`, {
                params: {
                    team_id: teamId,
                    shirt_number: value,
                    current_player_id: player.player_id
                }
            });

            if (response.data.success) {
                setShirtNumberError(response.data.available ? '' : response.data.message);
            } else {
                setShirtNumberError('Error checking shirt number availability');
            }
        } catch (error) {
            console.error('Error checking shirt number:', error);
            setShirtNumberError('Error checking shirt number availability');
        } finally {
            setIsCheckingShirtNumber(false);
        }
    };

    const handleShirtNumberChange = (e) => {
        const value = e.target.value;
        handleInputChange(e);

        if (shirtNumberCheckTimer.current) {
            clearTimeout(shirtNumberCheckTimer.current);
        }

        if (!value) {
            setShirtNumberError('');
            setIsCheckingShirtNumber(false);
            return;
        }

        shirtNumberCheckTimer.current = setTimeout(() => {
            checkShirtNumberAvailability(value);
        }, 400);
    };

    const handleShirtNumberBlur = (e) => {
        if (shirtNumberCheckTimer.current) {
            clearTimeout(shirtNumberCheckTimer.current);
        }
        checkShirtNumberAvailability(e.target.value);
    };
    
    const showUnlinkConfirmation = (parentId) => {
        setParentToUnlink(parentId);
        setShowConfirmation(true);
    };
    
    const handleUnlinkParent = async () => {
        if (!parentToUnlink) return;
        
        setIsUnlinking(true);
        setShowConfirmation(false);
        
        try {
            const response = await api.post(`${process.env.REACT_APP_API_URL}/unlink_parent.php`, {
                parent_id: parentToUnlink,
                player_id: player.player_id
            });
            
            if (response.data.success) {
                // Remove the unlinked parent from the state
                setLinkedParents(current => 
                    current.filter(parent => parent.parent_id !== parentToUnlink)
                );
            } else {
                setError(response.data.message || 'Failed to unlink parent');
            }
        } catch (error) {
            console.error('Error unlinking parent:', error);
            setError('An error occurred while unlinking the parent. Please try again.');
        } finally {
            setIsUnlinking(false);
            setParentToUnlink(null);
        }
    };

    const handleTogglePermission = async (parentId, currentPermission) => {
        setUpdatingPermission(true);
        
        try {
            const response = await api.post(`${process.env.REACT_APP_API_URL}/update_parent_permission.php`, {
                parent_id: parentId,
                player_id: player.player_id,
                can_respond_to_matches: !currentPermission
            });
            
            if (response.data.success) {
                // Update the parent's permission in the state
                setLinkedParents(current => 
                    current.map(parent => 
                        parent.parent_id === parentId 
                            ? { ...parent, can_respond_to_matches: !currentPermission }
                            : parent
                    )
                );
            } else {
                setError(response.data.message || 'Failed to update permission');
            }
        } catch (error) {
            console.error('Error updating permission:', error);
            setError('An error occurred while updating the permission. Please try again.');
        } finally {
            setUpdatingPermission(false);
        }
    };

    // Function to determine color based on stat value
    const getStatColor = (value) => {
        if (value >= 7) return '#4cd137'; // Bright green for high values (7-10)
        if (value >= 4) return '#ffa502'; // Bright orange for medium values (4-6)
        return '#ff4757'; // Bright red for low values (0-3)
    };

    return (
        <div className="modal-overlay">
            <div className="modal">
                <button 
                    className="modal-close"
                    onClick={onClose}
                >
                    ×
                </button>
                <h2>Edit Player</h2>
                {error && <div className="error-message">{error}</div>}
                
                <div className="modal-tabs">
                    <button 
                        type="button"
                        className={`tab ${activeTab === 'details' ? 'active' : ''} ${tabsWithErrors.includes('details') ? 'has-error' : ''}`}
                        onClick={() => setActiveTab('details')}
                    >
                        Details{tabsWithErrors.includes('details') ? ' *' : ''}
                    </button>
                    <button 
                        type="button"
                        className={`tab ${activeTab === 'stats' ? 'active' : ''} ${tabsWithErrors.includes('stats') ? 'has-error' : ''}`}
                        onClick={() => setActiveTab('stats')}
                    >
                        Stats{tabsWithErrors.includes('stats') ? ' *' : ''}
                    </button>
                    {!isFriendlyTeam && (
                        <button 
                            type="button"
                            className={`tab ${activeTab === 'parents' ? 'active' : ''}`}
                            onClick={() => setActiveTab('parents')}
                        >
                            Parents
                        </button>
                    )}
                </div>

                <form onSubmit={handleSubmit}>
                    {activeTab === 'details' ? (
                        <div className="tab-content">
                            <div className={`form-group ${fieldErrors.first_name ? 'has-error' : ''}`}>
                                <label>First Name *</label>
                                <input
                                    type="text"
                                    name="first_name"
                                    value={editForm.first_name}
                                    onChange={handleInputChange}
                                />
                                {fieldErrors.first_name && <span className="error-text">First name is required</span>}
                            </div>
                            <div className={`form-group ${fieldErrors.last_name ? 'has-error' : ''}`}>
                                <label>Last Name *</label>
                                <input
                                    type="text"
                                    name="last_name"
                                    value={editForm.last_name}
                                    onChange={handleInputChange}
                                />
                                {fieldErrors.last_name && <span className="error-text">Last name is required</span>}
                            </div>
                            {!hideContactFields && (
                                <>
                                    <div className={`form-group ${fieldErrors.phone ? 'has-error' : ''}`}>
                                        <label>Phone *</label>
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={editForm.phone}
                                            onChange={handleInputChange}
                                            placeholder="+44 7123 456789 or 07123 456789"
                                            maxLength="20"
                                        />
                                        {fieldErrors.phone && <span className="error-text">Phone is required</span>}
                                    </div>
                                    <div className="form-group">
                                        <label>Email</label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={editForm.email}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </>
                            )}
                            {hideContactFields && (
                                <div className="form-notice">
                                    <p>
                                        <FaInfoCircle className="info-icon" />
                                        Contact details are not shown for academy players aged 13 or younger.
                                    </p>
                                </div>
                            )}
                            <div className="form-group">
                                <label>Shirt Number</label>
                                <input
                                    type="number"
                                    name="shirt_number"
                                    value={editForm.shirt_number}
                                    onChange={handleShirtNumberChange}
                                    onBlur={handleShirtNumberBlur}
                                    min="1"
                                />
                                {isCheckingShirtNumber && <span className="checking-text">Checking availability...</span>}
                                {shirtNumberError && <span className="error-text">{shirtNumberError}</span>}
                            </div>
                        </div>
                    ) : activeTab === 'stats' ? (
                        <div className="tab-content">
                            <div className={`form-group ${fieldErrors.position ? 'has-error' : ''}`}>
                                <label>Position *</label>
                                <select
                                    name="position"
                                    value={editForm.position}
                                    onChange={handleInputChange}
                                >
                                    <option value="">Select Position</option>
                                    <option value="GK">Goalkeeper</option>
                                    <option value="CB">Center Back</option>
                                    <option value="LB">Left Back</option>
                                    <option value="RB">Right Back</option>
                                    <option value="CDM">Defending Midfielder</option>
                                    <option value="CM">Central Midfielder</option>
                                    <option value="CAM">Attacking Midfielder</option>
                                    <option value="LW">Left Winger</option>
                                    <option value="RW">Right Winger</option>
                                    <option value="ST">Striker</option>
                                </select>
                                {fieldErrors.position && <span className="error-text">Please select a position</span>}
                            </div>
                            <div className="form-group">
                                <label>Preferred Foot</label>
                                <select
                                    name="preferred_foot"
                                    value={editForm.preferred_foot}
                                    onChange={handleInputChange}
                                >
                                    <option value="Right">Right</option>
                                    <option value="Left">Left</option>
                                    <option value="Both">Both</option>
                                </select>
                            </div>
                            <div className="player-stats-editor">
                                <div className="rating-slider">
                                    <label>
                                        <span className="slider-label">Pace</span>
                                        <span className="slider-value">{editForm.pace / 10}<span className="scale-indicator">/10</span></span>
                                    </label>
                                    <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        step="10"
                                        name="pace"
                                        value={editForm.pace}
                                        onChange={handleInputChange}
                                        className="ability-slider"
                                        style={{
                                            '--value-percent': `${editForm.pace}%`,
                                            '--value-color': getStatColor(editForm.pace / 10)
                                        }}
                                    />
                                </div>
                                <div className="rating-slider">
                                    <label>
                                        <span className="slider-label">Shooting</span>
                                        <span className="slider-value">{editForm.shooting / 10}<span className="scale-indicator">/10</span></span>
                                    </label>
                                    <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        step="10"
                                        name="shooting"
                                        value={editForm.shooting}
                                        onChange={handleInputChange}
                                        className="ability-slider"
                                        style={{
                                            '--value-percent': `${editForm.shooting}%`,
                                            '--value-color': getStatColor(editForm.shooting / 10)
                                        }}
                                    />
                                </div>
                                <div className="rating-slider">
                                    <label>
                                        <span className="slider-label">Passing</span>
                                        <span className="slider-value">{editForm.passing / 10}<span className="scale-indicator">/10</span></span>
                                    </label>
                                    <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        step="10"
                                        name="passing"
                                        value={editForm.passing}
                                        onChange={handleInputChange}
                                        className="ability-slider"
                                        style={{
                                            '--value-percent': `${editForm.passing}%`,
                                            '--value-color': getStatColor(editForm.passing / 10)
                                        }}
                                    />
                                </div>
                                <div className="rating-slider">
                                    <label>
                                        <span className="slider-label">Dribbling</span>
                                        <span className="slider-value">{editForm.dribbling / 10}<span className="scale-indicator">/10</span></span>
                                    </label>
                                    <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        step="10"
                                        name="dribbling"
                                        value={editForm.dribbling}
                                        onChange={handleInputChange}
                                        className="ability-slider"
                                        style={{
                                            '--value-percent': `${editForm.dribbling}%`,
                                            '--value-color': getStatColor(editForm.dribbling / 10)
                                        }}
                                    />
                                </div>
                                <div className="rating-slider">
                                    <label>
                                        <span className="slider-label">Defending</span>
                                        <span className="slider-value">{editForm.defending / 10}<span className="scale-indicator">/10</span></span>
                                    </label>
                                    <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        step="10"
                                        name="defending"
                                        value={editForm.defending}
                                        onChange={handleInputChange}
                                        className="ability-slider"
                                        style={{
                                            '--value-percent': `${editForm.defending}%`,
                                            '--value-color': getStatColor(editForm.defending / 10)
                                        }}
                                    />
                                </div>
                                <div className="rating-slider">
                                    <label>
                                        <span className="slider-label">Physical</span>
                                        <span className="slider-value">{editForm.physical / 10}<span className="scale-indicator">/10</span></span>
                                    </label>
                                    <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        step="10"
                                        name="physical"
                                        value={editForm.physical}
                                        onChange={handleInputChange}
                                        className="ability-slider"
                                        style={{
                                            '--value-percent': `${editForm.physical}%`,
                                            '--value-color': getStatColor(editForm.physical / 10)
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="tab-content parents-tab">
                            <h3>Linked Parents</h3>
                            {linkedParents.length > 0 ? (
                                <div className="linked-parents-list">
                                    {linkedParents.map((parent, index) => (
                                        <div key={index} className="parent-item">
                                            <div className="parent-details">
                                                <div className="parent-name">{parent.first_name} {parent.last_name}</div>
                                                <div className="parent-contact">
                                                    <div className="contact-item">
                                                        <span className="label">Mobile:</span>
                                                        <span className="value">{parent.phone ? '0' + parent.phone : 'Not provided'}</span>
                                                    </div>
                                                    {parent.email && (
                                                        <div className="contact-item">
                                                            <span className="label">Email:</span>
                                                            <span className="value">{parent.email}</span>
                                                        </div>
                                                    )}
                                                    <div className="permission-item">
                                                        <span className="label">Can respond to matches / training availability:</span>
                                                        <div className="permission-toggle">
                                                            <input
                                                                type="checkbox"
                                                                id={`permission-${parent.parent_id}`}
                                                                checked={parent.can_respond_to_matches === 1 || parent.can_respond_to_matches === true}
                                                                onChange={() => handleTogglePermission(parent.parent_id, parent.can_respond_to_matches === 1 || parent.can_respond_to_matches === true)}
                                                                disabled={updatingPermission}
                                                                className="permission-checkbox"
                                                            />
                                                            <label htmlFor={`permission-${parent.parent_id}`} className="permission-label"></label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="parent-actions">
                                                <button 
                                                    type="button"
                                                    className="unlink-btn"
                                                    onClick={() => showUnlinkConfirmation(parent.parent_id)}
                                                    disabled={isUnlinking || updatingPermission}
                                                >
                                                    {isUnlinking ? 'Unlinking...' : 'Unlink'}
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="no-parents">
                                    <p>No parents linked to this player.</p>
                                    <p>Use the "Invite Parent" button on the players page to link a parent.</p>
                                </div>
                            )}
                        </div>
                    )}
                    
                    {activeTab !== 'parents' && (
                        <div className="modal-actions">
                            <button 
                                type="submit" 
                                disabled={shirtNumberError}
                                className={shirtNumberError ? 'disabled' : ''}
                            >
                                Save Changes
                            </button>
                        </div>
                    )}
                </form>
                
                {/* Confirmation Dialog */}
                {showConfirmation && (
                    <div className="confirmation-dialog-overlay">
                        <div className="confirmation-dialog">
                            <h4>Confirm Unlink</h4>
                            <p>Are you sure you want to unlink this parent from this player?</p>
                            <div className="confirmation-actions">
                                <button 
                                    type="button" 
                                    className="cancel-btn"
                                    onClick={() => {
                                        setShowConfirmation(false);
                                        setParentToUnlink(null);
                                    }}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="confirm-btn"
                                    onClick={handleUnlinkParent}
                                    disabled={isUnlinking}
                                >
                                    {isUnlinking ? 'Unlinking...' : 'Unlink Parent'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <style jsx>{`
                .player-stats-editor {
                    background-color: #222;
                    border-radius: 8px;
                    padding: 20px;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.3);
                    margin-top: 15px;
                }
                
                .parents-tab h3 {
                    margin-top: 0;
                    color: #ffd700;
                    margin-bottom: 20px;
                    text-align: center;
                }
                
                .linked-parents-list {
                    display: flex;
                    flex-direction: column;
                    gap: 15px;
                }
                
                .parent-item {
                    background-color: #222;
                    border-radius: 8px;
                    padding: 15px;
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                }
                
                .parent-name {
                    font-weight: bold;
                    color: #fff;
                    font-size: 1.1rem;
                    margin-bottom: 8px;
                    text-transform: capitalize;
                }
                
                .parent-contact {
                    display: flex;
                    flex-direction: column;
                    gap: 5px;
                }
                
                .contact-item {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 0.9rem;
                }
                
                .contact-item .label {
                    color: #a7a7a7;
                    min-width: 60px;
                }
                
                .contact-item .value {
                    color: #fff;
                }
                
                .permission-item {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 0.9rem;
                    margin-top: 8px;
                }
                
                .permission-toggle {
                    position: relative;
                    display: inline-block;
                }
                
                .permission-checkbox {
                    position: absolute;
                    opacity: 0;
                    width: 0;
                    height: 0;
                }
                
                .permission-label {
                    position: relative;
                    display: block;
                    width: 40px;
                    height: 20px;
                    background-color: #666;
                    border-radius: 20px;
                    cursor: pointer;
                    transition: background-color 0.3s ease;
                    box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.3);
                }
                
                .permission-label::before {
                    content: '';
                    position: absolute;
                    top: 2px;
                    left: 2px;
                    width: 16px;
                    height: 16px;
                    background-color: #fff;
                    border-radius: 50%;
                    transition: transform 0.3s ease;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
                }
                
                .permission-checkbox:checked + .permission-label {
                    background-color: #4CAF50;
                }
                
                .permission-checkbox:checked + .permission-label::before {
                    transform: translateX(20px);
                }
                
                .permission-checkbox:disabled + .permission-label {
                    opacity: 0.5;
                    cursor: not-allowed;
                }
                
                .parent-actions {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }
                
                .unlink-btn {
                    background-color: #d32f2f;
                    color: white;
                    border: none;
                    border-radius: 4px;
                    padding: 8px 15px;
                    cursor: pointer;
                    transition: background-color 0.3s;
                    font-size: 0.9rem;
                }
                
                .unlink-btn:hover {
                    background-color: #b71c1c;
                }
                
                .unlink-btn:disabled {
                    background-color: #888;
                    cursor: not-allowed;
                }
                
                .no-parents {
                    text-align: center;
                    padding: 30px 0;
                    color: #a7a7a7;
                }
                
                .no-parents p {
                    margin: 5px 0;
                }
                
                /* Confirmation Dialog Styles */
                .confirmation-dialog-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-color: rgba(0, 0, 0, 0.7);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    z-index: 1000;
                }
                
                .confirmation-dialog {
                    background-color: #333;
                    border-radius: 8px;
                    padding: 20px;
                    max-width: 400px;
                    width: 90%;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
                }
                
                .confirmation-dialog h4 {
                    color: #fff;
                    margin-top: 0;
                    margin-bottom: 15px;
                    font-size: 1.2rem;
                }
                
                .confirmation-dialog p {
                    color: #ddd;
                    margin-bottom: 20px;
                }
                
                .confirmation-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: 10px;
                }
                
                .cancel-btn {
                    background-color: #555;
                    color: #fff;
                    border: none;
                    border-radius: 4px;
                    padding: 8px 16px;
                    cursor: pointer;
                    transition: background-color 0.3s;
                }
                
                .cancel-btn:hover {
                    background-color: #444;
                }
                
                .confirm-btn {
                    background-color: #d32f2f;
                    color: #fff;
                    border: none;
                    border-radius: 4px;
                    padding: 8px 16px;
                    cursor: pointer;
                    transition: background-color 0.3s;
                }
                
                .confirm-btn:hover {
                    background-color: #b71c1c;
                }
                
                .confirm-btn:disabled {
                    background-color: #888;
                    cursor: not-allowed;
                }
                
                .rating-slider {
                    margin-bottom: 20px;
                }
                
                .rating-slider:last-child {
                    margin-bottom: 0;
                }
                
                .modal-tabs {
                    display: flex;
                    gap: 0;
                    margin-bottom: 20px;
                    border-bottom: 1px solid #444;
                }
                
                .modal-tabs .tab {
                    flex: 1;
                    padding: 12px 16px;
                    background: none;
                    border: none;
                    color: #a7a7a7;
                    cursor: pointer;
                    font-size: 14px;
                    font-weight: 500;
                    transition: all 0.3s ease;
                    border-bottom: 2px solid transparent;
                    min-width: 0;
                }
                
                .modal-tabs .tab:hover {
                    color: #fff;
                }
                
                .modal-tabs .tab.active {
                    color: #ffd700;
                    border-bottom-color: #ffd700;
                }

                .modal-tabs .tab.has-error {
                    color: #ff6b6b;
                }

                .modal-tabs .tab.has-error.active {
                    color: #ff8787;
                    border-bottom-color: #ff6b6b;
                }

                .form-group.has-error input,
                .form-group.has-error select {
                    border-color: #ff4757;
                }

                .form-group .error-text {
                    display: block;
                    color: #ff6b6b;
                    font-size: 0.85rem;
                    margin-top: 6px;
                }
                
                .rating-slider label {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 8px;
                    width: 100%;
                }
                
                .slider-label {
                    font-size: 14px;
                    font-weight: 500;
                    color: #fff;
                }
                
                .slider-value {
                    font-size: 16px;
                    font-weight: bold;
                    color: #ffd700;
                }
                
                .scale-indicator {
                    font-size: 12px;
                    color: #a7a7a7;
                    margin-left: 2px;
                }
                
                .ability-slider {
                    width: 100%;
                    height: 8px;
                    -webkit-appearance: none;
                    appearance: none;
                    background: #444;
                    border-radius: 4px;
                    outline: none;
                    position: relative;
                }
                
                .ability-slider::-webkit-slider-thumb {
                    -webkit-appearance: none;
                    appearance: none;
                    width: 24px;
                    height: 24px;
                    border-radius: 50%;
                    background: #fff;
                    cursor: pointer;
                    box-shadow: 0 0 5px rgba(0,0,0,0.3);
                    position: relative;
                    z-index: 2;
                }
                
                .ability-slider::-moz-range-thumb {
                    width: 24px;
                    height: 24px;
                    border-radius: 50%;
                    background: #fff;
                    cursor: pointer;
                    box-shadow: 0 0 5px rgba(0,0,0,0.3);
                    position: relative;
                    z-index: 2;
                    border: none;
                }
                
                .ability-slider::-webkit-slider-runnable-track {
                    height: 8px;
                    border-radius: 4px;
                    background: linear-gradient(to right, var(--value-color) 0%, var(--value-color) var(--value-percent), #444 var(--value-percent), #444 100%);
                }
                
                .ability-slider::-moz-range-track {
                    height: 8px;
                    border-radius: 4px;
                    background: #444;
                }
                
                .ability-slider::-moz-range-progress {
                    height: 8px;
                    border-radius: 4px;
                    background: var(--value-color);
                }
            `}</style>
        </div>
    );
}

export default EditPlayerModal; 